import { PrimaryButton, Tooltip, toast } from '@systemeio/ui';
import { BadRequest, isClient, isSioHost } from '@systemeio/util';
import DigitalProduct from 'modules/digital-product';
import { DigitalProductSourceEnum } from 'modules/digital-product/enums/digital-product-source-enum';
import { useOfferActions } from 'modules/digital-product/hooks/use-offer-actions';
import { handleOfferPricePlansErrors } from 'modules/digital-product/price-plans/utils/handle-offer-price-plans-errors';
import { FUNNELS_API } from 'modules/funnels/api/funnels-api';
import ProductWrapper from 'modules/funnels/funnel/components/offer-settings/product-wrapper';
import BaseConfiguration from 'modules/funnels/funnel/components/step-configuration-tab/base-configuration-tab/components/base-configuration';
import CouponSelector from 'modules/funnels/funnel/components/step-configuration-tab/coupon-selector/components/coupon-selector';
import OfferBumpSettings from 'modules/funnels/funnel/components/step-configuration-tab/offer-bump-settings';
import { OfferFormEditType, OfferFormViewInterface } from 'modules/funnels/funnel/components/step-configuration-tab/offer-form-configuration-tab/types/offer-form-interface';
import { getOfferSettingsApiData } from 'modules/funnels/funnel/components/step-configuration-tab/offer-form-configuration-tab/utils/get-offer-settings-api-data';
import PhysicalProductSelector from 'modules/funnels/funnel/components/step-configuration-tab/physical-product-selector/components/physical-product-selector';
import { OfferTypeEnum } from 'modules/funnels/funnel/enums/offer-type-enum';
import { useResetFunnelStepPageTemplate } from 'modules/funnels/funnel/hooks/use-reset-funnel-step-page-template';
import { useUpdateBaseConfigurationTab } from 'modules/funnels/funnel/hooks/use-update-base-configuration-tab';
import EditIcon from 'modules/funnels/funnel/icons/edit-icon';
import { BaseFunnelStepConfigurationEditInterface, BaseFunnelStepConfigurationInterface } from 'modules/funnels/funnel/types/base-funnel-step-configuration-settings-interface';
import { getSplitTestFunnelStepPreviewUrl } from 'modules/funnels/funnel/utils/page-utils';
import { FunnelId } from 'modules/funnels/types/funnel-interface';
import { usePaymentMethods } from 'modules/payment-methods/hooks/use-payment-methods';
import React from 'react';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
import { Loader } from 'shared/components/loader';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { getPageEditorUrl } from 'shared/utils/get-page-editor-url';
import { useFunnel } from '../../../../hooks/use-funnel';
import { useFunnelStepsList } from '../../../../hooks/use-funnel-steps-list';
import { FunnelStepInterface } from '../../../../types/funnel-step-interface';
import { getAvailableSaleLimitRedirectionFunnelSteps } from '../../../../utils/offer-configuration-utils';
import AffiliateCommission from '../../affiliate-commission/components/affiliate-commission';
import SaleLimit from '../../sale-limit/components/sale-limit';
import StepConfigurationTabLayout from '../../step-configuration-tab-layout';
import { useOfferFormSettings } from '../hooks/use-offer-form-settings';
interface OfferFormConfigurationTabProps {
  funnelStep: FunnelStepInterface;
  funnelId: FunnelId;
}
function OfferFormConfigurationTab({
  funnelStep,
  funnelId
}: OfferFormConfigurationTabProps) {
  const {
    funnel
  } = useFunnel(funnelId);
  const {
    data: paymentMethodsData
  } = usePaymentMethods(funnel?.currency);
  const {
    offerFormSettings,
    mutateOfferFormSettings
  } = useOfferFormSettings(funnelStep.id);
  const {
    funnelStepsList,
    mutateFunnelStepsList
  } = useFunnelStepsList(funnelId);
  const {
    t
  } = useLocoTranslation();
  const {
    updateOffer
  } = useOfferActions();
  const {
    updateFetcher: updateBaseFetcher
  } = useUpdateBaseConfigurationTab<BaseFunnelStepConfigurationInterface>({
    funnelStepId: funnelStep.id,
    funnelId: funnelId,
    updateUrl: `${FUNNELS_API}/steps/${funnelStep.id}`
  });
  const {
    resetFunction
  } = useResetFunnelStepPageTemplate({
    funnelId,
    funnelStepId: funnelStep.id
  });
  const saveOffer = async (offer: OfferFormEditType) => {
    if (!offerFormSettings) return;
    try {
      const data = await updateOffer(offerFormSettings?.offer.id, offer);
      await mutateOffer(data);
      toast.success(t('global.changes_saved'));
    } catch (e) {
      if (e instanceof BadRequest) {
        handleOfferPricePlansErrors(e.errors);
      }
      throw e;
    }
  };
  const mutateOffer = async (offer_0: OfferFormViewInterface['offer']) => {
    await mutateOfferFormSettings(mutationData => {
      if (mutationData) {
        return {
          ...mutationData,
          offer: offer_0
        };
      }
    }, false);
  };
  const handleSaveOffer = async (updateOffer_0: (offer: OfferFormEditType) => OfferFormEditType) => {
    if (!offerFormSettings) return;
    await saveOffer(updateOffer_0(getOfferSettingsApiData(offerFormSettings.offer)));
  };
  const handleSaveBaseProperties = async (baseData: BaseFunnelStepConfigurationEditInterface) => {
    await updateBaseFetcher<BaseFunnelStepConfigurationEditInterface>(baseData, async () => {
      await mutateFunnelStepsList(data_0 => {
        if (data_0) {
          return data_0.map(dataFunnelStep => {
            if (dataFunnelStep.id === funnelStep.id) {
              return {
                ...dataFunnelStep,
                name: baseData.name
              };
            }
            return dataFunnelStep;
          });
        }
      }, false);
      await mutateOfferFormSettings(data_1 => {
        if (data_1) {
          return {
            ...data_1,
            name: baseData.name,
            page: {
              ...data_1.page,
              path: baseData.page.path
            }
          };
        }
      }, false);
    });
  };
  if (!offerFormSettings || !funnel || !funnelStep.page || !paymentMethodsData) return <div className={'flex h-full items-center justify-center py-12'}>
        <Loader className={'scale-150'} />
      </div>;
  return <div className="flex flex-col gap-5">
      <StepConfigurationTabLayout viewLinkUrl={getSplitTestFunnelStepPreviewUrl(funnel.domain.domainName, offerFormSettings.page.path, funnelStep.inRunningSplitTest)} editLinkUrl={getPageEditorUrl(funnelStep.page.id)} customEditButton={<>
            {funnelStep.inRunningSplitTest ? <Tooltip mode="hover" label={t('dashboard.funnel.configuration.common.in_split_test_not_available')} className={'overflow-auto'}>
                <PrimaryButton className="w-full !justify-start" disabled data-test-element={'edit-page-funnel-step-button'}>
                  <EditIcon className={'fill-background-secondary h-[18px] w-[18px]'} />
                  <span className="text-start">
                    {t('dashboard.funnel.configuration.common.edit_page')}
                  </span>
                </PrimaryButton>
              </Tooltip> : <PrimaryButton as={LinkWithoutPrefetch} className="!justify-start" href={getPageEditorUrl(funnelStep.page.id)} target={'_self'} data-test-element={'edit-page-funnel-step-button'}>
                <EditIcon className={'fill-background-secondary h-[18px] w-[18px]'} />
                <span className="text-start">
                  {t('dashboard.funnel.configuration.common.edit_page')}
                </span>
              </PrimaryButton>}
          </>} onChangeTemplate={resetFunction}>
        <div className="flex flex-col gap-5">
          <BaseConfiguration path={offerFormSettings.page.path} name={offerFormSettings.name} domainName={funnel.domain.domainName} pathDisabled={funnelStep.inRunningSplitTest} onSave={handleSaveBaseProperties} />
          <hr className="border-text-primary/15 mb-7 mt-7 border-solid" />
          {isClient() && isSioHost(window.location.host) && <AffiliateCommission affiliateCommission={offerFormSettings.offer.affiliateCommission} payoutDelay={offerFormSettings.offer.payoutDelay} onSave={data_2 => handleSaveOffer(prev => ({
          ...prev,
          ...data_2
        }))} />}
          <SaleLimit saleLimit={offerFormSettings.offer.saleLimit} funnelStepId={funnelStep.id} saleLimitFunnelStepRedirect={offerFormSettings.offer.saleLimitFunnelStepRedirect} saleLimitFunnelStepData={getAvailableSaleLimitRedirectionFunnelSteps(funnelStep.id, funnelStepsList)} onSave={data_3 => handleSaveOffer(prev_0 => ({
          ...prev_0,
          ...data_3
        }))} />
          <hr className="border-text-primary/15 mt-7 border-solid" />
        </div>
      </StepConfigurationTabLayout>
      <div className="flex w-full grow flex-col gap-5 pb-10 lg:px-8 ">
        <ProductWrapper digitalProduct={offerFormSettings.offer.digitalProduct} product={offerFormSettings.offer.product}>
          {type => <>
              {type === OfferTypeEnum.physical && <PhysicalProductSelector product={offerFormSettings?.offer.product?.id || null} onSave={data_4 => handleSaveOffer(prev_1 => ({
            ...prev_1,
            digitalProduct: null,
            product: data_4
          }))} funnel={funnel} />}
              {type === OfferTypeEnum.digital && <DigitalProduct source={DigitalProductSourceEnum.Funnel} product={offerFormSettings.offer.digitalProduct} onSave={data_5 => handleSaveOffer(prev_2 => ({
            ...prev_2,
            product: null,
            digitalProduct: data_5
          }))} currency={funnel.currency} paymentMethods={paymentMethodsData.paymentMethods} />}
            </>}
        </ProductWrapper>
        <CouponSelector currency={funnel.currency} data={offerFormSettings?.offer.coupons} onSave={data_6 => handleSaveOffer(prev_3 => ({
        ...prev_3,
        coupons: data_6
      }))} />
        <OfferBumpSettings funnel={funnel} offerBumps={offerFormSettings.offer.bumps} onSaveBump={data_7 => handleSaveOffer(prev_4 => ({
        ...prev_4,
        bumps: data_7
      }))} paymentMethods={paymentMethodsData.paymentMethods} />
      </div>
    </div>;
}
export default OfferFormConfigurationTab;